/**
 * Project Archive Modal Hook
 *
 * Manages state and logic for the project archive modal.
 * This modal appears when a user with multiple projects downgrades to Free plan
 * or when actionType is FEATURE_LIMIT_EXCEEDED with projects exceeded.
 *
 * @module @ideoz/core/hooks/facades/shared/useProjectArchiveModal
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { selectProjects, getUserProjectsWithPagination } from '../../../store/slices/projectSlice';
import { projectService } from '../../../api/services/project.service';
import apiClient from '../../../api/client';
import { ApiUrls } from '../../../api/ApiUrls';
import { isFreeModeEnabled } from '../../../utils/free-mode';
import { isWebBuild } from '../../../utils/environment';
import type { NavigationFacade, ShowToastFn } from './types';
import type { ProjectForArchive } from '../../../types/project.types';
import type { PlanDetails, BulkUpdateProjectItem } from '../../../types/subscription.types';

/**
 * State returned by useProjectArchiveModal hook
 */
export interface ProjectArchiveModalState {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * List of projects to display in selection
   */
  projects: ProjectForArchive[];

  /**
   * Whether the archive operation is processing
   */
  isProcessing: boolean;

  /**
   * Handler for "Upgrade instead" action
   */
  onUpgradeInstead: () => void;

  /**
   * Handler for "Keep project" action
   */
  onKeepProject: (selectedProjectId: string) => void;

  /**
   * Open the modal programmatically (used when subscription modal triggers it)
   */
  openModal: () => void;

  /**
   * Close the modal programmatically
   */
  closeModal: () => void;
}

/**
 * Parameters for useProjectArchiveModal hook
 */
export interface UseProjectArchiveModalParams {
  /**
   * Navigation facade for redirecting
   */
  navigation: NavigationFacade;

  /**
   * Plan details from user profile (contains subscriptionInfo)
   */
  planDetails?: PlanDetails | null;

  /**
   * Toast notification function
   */
  showToast?: ShowToastFn;

  /**
   * Callback to handle upgrade flow - used in plugin to open web billing
   * If not provided, web will use Stripe checkout flow
   */
  onUpgrade?: () => void;

  /**
   * Smart plan checkout ID (periodicPlans[0].id) for Stripe checkout on web
   * If not provided, will attempt to fetch from available plans API
   */
  smartPlanId?: string;
}

/**
 * Hook to manage project archive modal state and actions
 *
 * The modal is triggered when:
 * 1. planDetails.subscriptionInfo.actionType === 'FEATURE_LIMIT_EXCEEDED'
 *    AND exceededFeatures contains 'projects'
 * 2. User clicks "Proceed to downgrade" in subscription expired modal with >1 project
 *
 * @param params - Configuration parameters
 * @returns Project archive modal state and handlers
 *
 * @example
 * ```tsx
 * const projectArchiveModal = useProjectArchiveModal({
 *   navigation,
 *   planDetails,
 *   showToast,
 *   onUpgrade: handleStripeUpgrade,
 * });
 *
 * <ProjectArchiveModal
 *   isOpen={projectArchiveModal.isOpen}
 *   projects={projectArchiveModal.projects}
 *   onUpgradeInstead={projectArchiveModal.onUpgradeInstead}
 *   onKeepProject={projectArchiveModal.onKeepProject}
 *   isProcessing={projectArchiveModal.isProcessing}
 * />
 * ```
 */
export function useProjectArchiveModal({
  navigation,
  planDetails,
  showToast,
  onUpgrade,
  smartPlanId,
}: UseProjectArchiveModalParams): ProjectArchiveModalState {
  const dispatch = useAppDispatch();
  const reduxProjects = useAppSelector(selectProjects);
  const isWeb = isWebBuild();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasAutoTriggered, setHasAutoTriggered] = useState(false);
  const [availablePlanId, setAvailablePlanId] = useState<string | null>(null);

  // Transform redux projects to ProjectForArchive format
  const projects: ProjectForArchive[] = useMemo(() => {
    return reduxProjects.map((project) => ({
      id: project.id,
      name: project.name || 'Untitled Project',
      fileCount: (project as any).fileCount ?? (project as any).file_count,
      updatedAt: (project as any).updatedAt ?? (project as any).updated_at ?? project.created_on,
    }));
  }, [reduxProjects]);

  // Check if projects feature is exceeded
  const isProjectsExceeded = useMemo(() => {
    const subscriptionInfo = planDetails?.subscriptionInfo;
    if (subscriptionInfo?.actionType !== 'FEATURE_LIMIT_EXCEEDED') {
      return false;
    }
    const exceededFeatures = subscriptionInfo.exceededFeatures || [];
    return exceededFeatures.some(
      (feature) => feature.name.toLowerCase() === 'projects'
    );
  }, [planDetails]);

  // Fetch available plans to get Smart plan checkout ID (periodicPlans[0].id) for Stripe checkout (web only)
  useEffect(() => {
    // FREE_MODE_ENABLED bypass — no Stripe checkout while billing is off.
    if (isFreeModeEnabled()) return;
    if (isWeb && !onUpgrade && !smartPlanId && !availablePlanId) {
      const fetchPlans = async () => {
        try {
          const response = await apiClient.get(ApiUrls.getPlans);
          const plansData = response.data?.data || response.data || [];
          // Find the Smart plan
          const smartPlan = plansData.find((plan: any) => 
            plan.name?.toLowerCase() === 'smart' || 
            plan.planName?.toLowerCase() === 'smart'
          );
          if (smartPlan) {
            // Get the checkout ID from periodicPlans[0].id (first active or first available)
            const periodicPlan = smartPlan.periodicPlans?.find((pp: any) => pp.active) || smartPlan.periodicPlans?.[0];
            const checkoutId = periodicPlan?.id;
            if (checkoutId) {
              setAvailablePlanId(checkoutId);
            }
          }
        } catch (error) {
          console.error('Failed to fetch plans:', error);
        }
      };
      fetchPlans();
    }
  }, [isWeb, onUpgrade, smartPlanId, availablePlanId]);

  // Auto-trigger modal when projects feature is exceeded and user has >1 project
  useEffect(() => {
    if (isProjectsExceeded && projects.length > 1 && !isOpen && !hasAutoTriggered) {
      setIsOpen(true);
      setHasAutoTriggered(true);
    }
  }, [isProjectsExceeded, projects.length, isOpen, hasAutoTriggered]);

  // Close modal and reset trigger when condition is no longer met (e.g., fresh data loaded)
  useEffect(() => {
    if (!isProjectsExceeded && isOpen && hasAutoTriggered) {
      // Fresh data indicates projects are no longer exceeded - close the modal
      // Only close if it was auto-triggered, not if manually opened
      setIsOpen(false);
      setHasAutoTriggered(false);
    }
  }, [isProjectsExceeded, isOpen, hasAutoTriggered]);

  // Open modal programmatically (called from subscription expired modal)
  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Close modal
  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Handler for "Upgrade instead" button
  const handleUpgradeInstead = useCallback(async () => {
    // Plugin: Use provided callback to open web billing
    if (onUpgrade) {
      onUpgrade();
      return;
    }

    // Web: Use Stripe checkout flow
    if (isWeb) {
      const planId = smartPlanId || availablePlanId;
      
      if (!planId) {
        // Fallback: Navigate to billing page if no plan ID available
        if (showToast) {
          showToast({
            message: 'Redirecting to billing page...',
            severity: 'info',
          });
        }
        if (navigation.toBilling) {
          navigation.toBilling();
        }
        return;
      }

      setIsProcessing(true);

      try {
        // Build success and cancel URLs for Stripe redirect
        const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
        const successUrl = `${baseUrl}/billing?payment=success`;
        const cancelUrl = `${baseUrl}/billing?payment=cancelled`;

        // Build the API URL
        const checkoutApiUrl = ApiUrls.getCheckoutUrl(planId, successUrl, cancelUrl);

        if (showToast) {
          showToast({
            message: 'Preparing checkout...',
            severity: 'info',
          });
        }

        // Call the backend API to get the Stripe checkout URL
        const response = await apiClient.get(checkoutApiUrl, {
          maxRedirects: 0,
          validateStatus: (status) => status >= 200 && status < 400,
        });

        // Get the Stripe checkout URL from the response
        const stripeCheckoutUrl = response.data?.url;

        if (!stripeCheckoutUrl) {
          throw new Error('No checkout URL received from server');
        }

        // Close the modal before redirect
       // setIsOpen(false);
       setIsProcessing(false);

        if (showToast) {
          showToast({
            message: 'Redirecting to Stripe checkout...',
            severity: 'info',
          });
        }

        // Small delay to show the toast, then redirect to Stripe
        await new Promise(resolve => setTimeout(resolve, 300));

        // Redirect to Stripe checkout page
        window.location.href = stripeCheckoutUrl;

      } catch (error: any) {
        console.error('Error initiating upgrade:', error);

        // Check if the error response contains a redirect URL
        if (error.response?.headers?.location) {
          const redirectUrl = error.response.headers.location;
          setIsOpen(false);
          if (showToast) {
            showToast({
              message: 'Redirecting to Stripe checkout...',
              severity: 'info',
            });
          }
          await new Promise(resolve => setTimeout(resolve, 300));
          window.location.href = redirectUrl;
          return;
        }

        if (showToast) {
          showToast({
            message: error.message || 'Failed to start upgrade. Please try again.',
            severity: 'error',
          });
        }
        setIsProcessing(false);
      }
    } else {
      // Fallback: Navigate to billing page
      if (navigation.toBilling) {
        navigation.toBilling();
      }
    }
  }, [onUpgrade, isWeb, smartPlanId, availablePlanId, showToast, navigation]);

  // Handler for "Keep project" button
  const handleKeepProject = useCallback(async (selectedProjectId: string) => {
    setIsProcessing(true);

    try {
      // Get list of projects to archive (all except selected)
      const projectsToArchive = projects.filter(
        (project) => project.id !== selectedProjectId
      );

      if (projectsToArchive.length === 0) {
        // No projects to archive
        setIsProcessing(false);
        setIsOpen(false);
        return;
      }

      // Build bulk update payload
      const updates: BulkUpdateProjectItem[] = projectsToArchive.map((project) => ({
        id: project.id,
        data: { isActive: false },
      }));

      // Call bulk update API
      const response = await projectService.bulkUpdateProjects({projects: updates});

      // Success
      if (showToast) {
        showToast({
          message: `Successfully archived ${response.updatedCount} project(s)`,
          severity: 'success',
        });
      }

      // Refresh projects list
      dispatch(getUserProjectsWithPagination({ limit: 50, page: 1 }));

      // Close modal
      setIsOpen(false);
      setIsProcessing(false);

      // Navigate to projects page to show updated list
      if (navigation.toProjectsList) {
        navigation.toProjectsList();
      }
    } catch (error) {
      console.error('Failed to archive projects:', error);
      
      if (showToast) {
        showToast({
          message: error instanceof Error ? error.message : 'Failed to archive projects',
          severity: 'error',
        });
      }
      
      setIsProcessing(false);
    }
  }, [projects, showToast, dispatch, navigation]);

  return {
    isOpen,
    projects,
    isProcessing,
    onUpgradeInstead: handleUpgradeInstead,
    onKeepProject: handleKeepProject,
    openModal,
    closeModal,
  };
}
